import React, { useState } from "react";
import { useMediaQuery } from "@mui/material";
import ErrorIcon from "@mui/icons-material/Error";
import { DroppsTooltip } from "@/components/DroppsTooltip";
import theme from "@/theme";
import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Money } from "@/helpers/Money";
import { cn } from "@/helpers/utils";

type OrderTotalProps = {
  subtotal: Money;
  shippingCost: Money;
  discount?: { amount: Money; name: string };
  estimatedTotal: Money;
  className?: string;
};

export const OrderTotals = ({
  subtotal,
  shippingCost,
  discount,
  estimatedTotal,
  className,
}: OrderTotalProps) => {
  const isMobile: boolean = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.md)
  );

  const [shipAlertDialogOpen, setShipAlertDialogOpen] = useState(false);
  const isFreeShipping = shippingCost == Money.zero;
  const shipAlert = (
    <div className="item-center flex space-x-4 md:items-start md:space-x-2 md:p-2">
      <ErrorIcon htmlColor={theme.palette.warning.main} />
      <p className="body text-sm leading-normal md:leading-tight">
        To get free shipping on this box it must be over $35. Adjust your item
        quantity, or add another item.
      </p>
    </div>
  );

  return (
    <div className={cn("grid grid-cols-4", className)}>
      <p className="body col-span-3 text-sm">Subtotal:</p>
      <p className="body text-right text-sm">{subtotal.format()}</p>
      {!!discount ? (
        <>
          <p className="body col-span-3 text-sm italic">
            {discount.name.toLocaleUpperCase()}:
          </p>
          <p className="body text-right text-sm italic">
            -{discount.amount.format()}
          </p>
        </>
      ) : null}
      <p className="body col-span-3 w-full text-sm md:block">
        <span>Shipping:</span>{" "}
        {!isFreeShipping && !isMobile && (
          <DroppsTooltip title={shipAlert}>
            <ErrorIcon
              className="align-middle text-gray-700"
              style={{ fontSize: "var(--font-size-md)" }}
            />
          </DroppsTooltip>
        )}
        {!isFreeShipping && isMobile && (
          <>
            <Button
              variant="text"
              onClick={() => setShipAlertDialogOpen(true)}
              className="mt-1 inline w-full border-0 text-left text-xs font-normal capitalize tracking-normal underline"
            >
              How to get free shipping?
            </Button>
            <Dialog
              open={shipAlertDialogOpen}
              onClose={() => setShipAlertDialogOpen(false)}
              title={
                <span className="text-gray-700">How to get free shipping?</span>
              }
            >
              {shipAlert}
            </Dialog>
          </>
        )}
      </p>
      <p className="body text-right text-sm">{shippingCost.format()}</p>
      <p className="body col-span-3 row-span-3 pt-2 font-semibold">
        Estimated Total:
      </p>
      <p className="body row-span-3 pt-2 text-right font-semibold">
        {estimatedTotal.format()}
      </p>
    </div>
  );
};
